import { useEffect, useRef, useState } from 'react'
import { cx } from './cx'
import { Avatar } from './Avatar'
import { Icon, type IconName } from './Icon'
import styles from './AccountMenu.module.css'

export interface AccountOption {
  id: string
  label: string
  meta?: string
  icon: IconName
  active?: boolean
}

interface AccountMenuProps {
  name: string
  subtitle: string
  options: AccountOption[]
  onPick: (id: string) => void
  onSignOut: () => void
  /** Small print under the actions, e.g. the commit SHA. */
  footnote?: string
}

/** Sidebar footer: avatar + name, with a popover (above) to switch condominium / scope and sign out. */
export function AccountMenu({
  name,
  subtitle,
  options,
  onPick,
  onSignOut,
  footnote,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className={styles.wrap}>
      {open && (
        <div className={styles.popover} role="menu" aria-label="Condomínio e escopo">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              role="menuitemradio"
              aria-checked={!!o.active}
              className={cx(styles.option, o.active && styles.active)}
              onClick={() => {
                setOpen(false)
                onPick(o.id)
              }}
            >
              <Icon name={o.icon} size={16} className={styles.optionIcon} />
              <span className={styles.optionText}>
                <span className={styles.optionLabel}>{o.label}</span>
                {o.meta && <span className={styles.meta}>{o.meta}</span>}
              </span>
            </button>
          ))}
          <div className={styles.divider} />
          <button
            type="button"
            role="menuitem"
            className={styles.option}
            onClick={() => {
              setOpen(false)
              onSignOut()
            }}
          >
            <Icon name="logout" size={16} className={styles.optionIcon} />
            <span className={styles.optionLabel}>Sair</span>
          </button>
          {footnote && <div className={styles.footnote}>{footnote}</div>}
        </div>
      )}
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Avatar name={name} size={34} />
        <span className={styles.text}>
          <span className={styles.name}>{name}</span>
          <span className={styles.subtitle}>{subtitle}</span>
        </span>
        <Icon name="swap_vert" size={17} color="var(--color-text-tertiary)" />
      </button>
    </div>
  )
}
